import React, { useEffect, useMemo, useState } from "react";
import { getIntegrationData } from "../api/integrations";
import Sidebar from "../components/Sidebar";
import MainContent from "../components/MainContent";

const Main = (props) => {
  const [data, setData] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [message, setMessage] = useState("Loading");
  const [showUnfiltered, setShowUnfiltered] = useState(true);

  const fetchData = async () => {
    try {
      const incoming = await getIntegrationData();

      // helper: get numeric timestamp
      const getTs = (item) => {
        if (!item) {
          return 0
        }
        const v =
          (item.body && item.body.entireEvent && item.body.entireEvent["Event-Date-Timestamp"]) ??
          0;
        const n = Number(v);
        return Number.isFinite(n) ? n : 0;
      };

      // Step 1: sort by timestamp (oldest first) so numbering increases with time
      const chronologicallySorted = Array.isArray(incoming)
        ? [...incoming].sort((a, b) => getTs(a) - getTs(b))
        : incoming;

      // Step 2: assign call IDs in chronological order
      const legToGroup = {};
      let groupCounter = 1;

      const assignGroup = (uuid, otherLeg) => {
        if (legToGroup[uuid]) return legToGroup[uuid];
        if (otherLeg && legToGroup[otherLeg]) {
          legToGroup[uuid] = legToGroup[otherLeg];
          return legToGroup[uuid];
        }
        const id = groupCounter++;
        legToGroup[uuid] = id;
        if (otherLeg) legToGroup[otherLeg] = id;
        return id;
      };

      const enriched =
        Array.isArray(chronologicallySorted) &&
        chronologicallySorted.map((evt) => {
          const body = evt.body || {};
          const entireEvent = body.entireEvent || {};
          const uuid = entireEvent["Unique-ID"] || null;
          const otherLeg = entireEvent["Other-Leg-Unique-ID"] || null;

          // Id for the whole call
          const callId = uuid ? assignGroup(uuid, otherLeg) : null;

          // Leg type: A/B/unknown
          let legType = "unknown";
          const dir = entireEvent["Call-Direction"] || "unknown";
          if (dir === "inbound") legType = "A";
          else if (dir === "outbound") legType = "B";

          return {
            ...evt,
            callId,
            legType,
          };
        });

      // Step 3: sort again the newest first for UI display
      const sorted = Array.isArray(enriched)
        ? [...enriched].sort((a, b) => getTs(b) - getTs(a))
        : enriched;

      setData(sorted);

      setSelectedIndex((prev) => {
        if (!Array.isArray(sorted) || sorted.length === 0) {
          return 0
        }
        return Math.min(Math.max(prev, 0), sorted.length - 1);
      });
      setMessage("");
    } catch (e) {
      setMessage("Error: unable to load in data");
      console.error(e);
      localStorage.removeItem("token");
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Helper to determine if an item is explicitly NOT filtered out by webhook filter (flag === false)
  const isNonFiltered = (item) => {
    try {
      const rawBody = item?.body;
      const obj = typeof rawBody === 'string' ? JSON.parse(rawBody) : rawBody;
      const u = obj && typeof obj === 'object' ? obj.usedWebhookFilter : undefined;
      if (u && typeof u === 'object' && 'isFilteredOutByWebhookFilter' in u) {
        return u.isFilteredOutByWebhookFilter === true;
      }
    } catch (_) {
      // ignore parse errors
    }
    return false;
  };

  // Derive filtered data according to the toggle:
  // - When showUnfiltered is true: include all items
  // - When false: hide events where isFilteredOutByWebhookFilter === false
  const filteredData = useMemo(() => {
    if (!Array.isArray(data)) return data;
    if (showUnfiltered) return data;
    return data.filter((it) => !isNonFiltered(it));
  }, [data, showUnfiltered]);

  // Clamp selected index when data set changes due to filtering
  useEffect(() => {
    if (!Array.isArray(filteredData) || filteredData.length === 0) {
      setSelectedIndex(0);
      return;
    }
    setSelectedIndex((prev) => Math.min(Math.max(prev, 0), filteredData.length - 1));
  }, [filteredData]);

  return (
    <div className="app-container">
      <Sidebar
        data={filteredData}
        setSearchTerm={setSearchTerm}
        searchTerm={searchTerm}
        selectedIndex={selectedIndex}
        setSelectedIndex={setSelectedIndex}
        fetchData={fetchData}
        showUnfiltered={showUnfiltered}
        setShowUnfiltered={setShowUnfiltered}
      />
      <MainContent
        data={filteredData || []}
        selectedIndex={selectedIndex}
        searchTerm={searchTerm}
        message={message}
        fetchData={fetchData}
      />
    </div>
  );
};

export default Main;
